/**
 * InvoiceEditPage - Invoice editing with compound hook architecture
 *
 * This page uses the useInvoicePageData compound hook pattern to:
 * - Eliminate complex hook ordering requirements
 * - Reduce component complexity from 469 → ~200 lines
 * - Provide clean separation of concerns
 * - Centralize interdependent data management
 *
 * Migrated from the original implementation to address Claude review feedback
 * regarding hook dependencies and tight coupling concerns.
 */

import React, { useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { executeStatusUpdateOperation } from '@utils/errorHandling';
import { InvoiceHeader, PdfViewer, NoPdfAvailable } from '@organisms/invoice';
import { SplitPanelTemplate, InvoiceFormTemplate } from '@templates';
import { useInvoicePageData, useInvoiceMutations } from '@hooks/useInvoicePageData';
import { usePermission, INVOICE_PERMISSIONS } from '@hooks/usePermissions';
import toast from 'react-hot-toast';
import { trpc } from '@lib/trpc';
import { UserRole } from '@config/workflow-config';
import { getReturnUrl } from '@utils/navigationHelpers';

// Helper function to map user roles to workflow roles
function getUserWorkflowRole(userRoles: { name: string }[] | undefined): UserRole {
  if (!userRoles || userRoles.length === 0) {
    return UserRole.STOCK; // Default to lowest permissions
  }

  // Check for admin role first (check exact matches first, then partial)
  const adminRole = userRoles.find(
    role =>
      role.name === 'admin' ||
      role.name === 'administrator' ||
      role.name.toLowerCase().includes('admin')
  );
  if (adminRole) {
    return UserRole.ADMINISTRATOR;
  }

  // Check for finance role
  const financeRole = userRoles.find(
    role => role.name === 'finance' || role.name.toLowerCase().includes('finance')
  );
  if (financeRole) {
    return UserRole.FINANCE;
  }

  // Check for stock role
  const stockRole = userRoles.find(
    role => role.name === 'stock' || role.name.toLowerCase().includes('stock')
  );
  if (stockRole) {
    return UserRole.STOCK;
  }

  // Default to stock role if no matching roles found
  return UserRole.STOCK;
}

export const InvoiceEditPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isMovingToStep, setIsMovingToStep] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Single compound hook provides all data and state management
  const {
    // Core data
    id,
    navigate,
    user,
    invoice,
    storesData,
    suppliersData,
    pdfUrl,

    // Form management
    formData,
    setFormData,
    currentStatus,
    setCurrentStatus,
    selectedStore,
    setSelectedStore,
    selectedSupplier,
    setSelectedSupplier,
    buildInvoicePayload,
    handleInputChange,

    // Permissions - automatically spread from compound hook
    hasRole,
    canEditStockFields,
    canEditAddingStockFields,
    canEditWorkflowFields,
    isBasicFieldDisabled,
    isWorkflowFieldDisabled,
    isNotesFieldDisabled,
    isCreditNoteFieldDisabled,
    wasUserPreviouslyAssigned,

    // Status info - automatically spread
    nextStatus,
    nextStepButtonText,
    statusDisplayName,

    // Loading states
    isLoading: isDataLoading,
    isLoadingStores,
    isLoadingSuppliers,
    isLoadingPdf,
    isDataReady,

    // Utilities
    utils,
    invalidateInvoiceCache,
  } = useInvoicePageData();

  // Check if user has permission to delete invoices AND invoice is in unprocessed status
  const { hasPermission: hasDeletePermission } = usePermission(INVOICE_PERMISSIONS.DELETE);
  const canDeleteInvoice = hasDeletePermission && currentStatus === 'unprocessed';

  // Determine user workflow role for the new permission system
  const userWorkflowRole = getUserWorkflowRole(user?.roles) || UserRole.STOCK;

  // Separate mutations hook to avoid re-renders
  const { updateStatusMutation, updateInvoiceMutation, assignToMeMutation, unassignMutation } =
    useInvoiceMutations(
      id,
      setCurrentStatus,
      utils,
      invalidateInvoiceCache,
      navigate,
      getReturnUrl(searchParams)
    );

  // Delete mutation using trpc
  const deleteInvoiceMutation = trpc.invoice.delete.useMutation({
    onSuccess: () => {
      toast.success('Invoice deleted successfully');
      // Invalidate cache to update invoice list and status badge counts
      invalidateInvoiceCache();
      const returnUrl = getReturnUrl(searchParams);
      navigate(returnUrl);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete invoice');
    },
  });

  // Business logic handlers
  const handleSave = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      await updateInvoiceMutation.mutateAsync({
        id,
        data: buildInvoicePayload,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, updateInvoiceMutation, buildInvoicePayload]);

  const handleCancel = useCallback(() => {
    const returnUrl = getReturnUrl(searchParams);
    navigate(returnUrl);
  }, [navigate, searchParams]);

  // Status override handler with proper error handling
  const handleStatusOverride = useCallback(
    async (newStatus: string) => {
      if (!id) return;

      try {
        // Create a permission-safe payload for override operations
        const safePayload = { ...buildInvoicePayload };

        // Remove notes field if user doesn't have permission to edit notes
        if (isNotesFieldDisabled) {
          delete safePayload.notes;
        }

        // Use the enhanced error handling system
        await executeStatusUpdateOperation({
          saveOperation: async () => {
            await updateInvoiceMutation.mutateAsync({
              id,
              data: safePayload,
            });
          },
          statusUpdateOperation: async () => {
            await updateStatusMutation.mutateAsync({
              id,
              status: newStatus,
              isAdminOverride: true,
            });
          },
          shouldUnassign: false, // Admin overrides don't auto-unassign
          unassignOperation: async () => {
            await unassignMutation.mutateAsync({ id });
          },
        });
      } catch (error) {
        // Status override failed - silently handle
      }
    },
    [
      id,
      buildInvoicePayload,
      updateInvoiceMutation,
      updateStatusMutation,
      unassignMutation,
      isNotesFieldDisabled,
    ]
  );

  // Workflow progression handler
  const handleNextStatus = useCallback(async () => {
    if (!id || !nextStatus) return;

    setIsMovingToStep(true);
    try {
      // Step 1: Save form changes
      await updateInvoiceMutation.mutateAsync({
        id,
        data: buildInvoicePayload,
      });

      // Step 2: Update status (no unassign needed for normal workflow)
      await updateStatusMutation.mutateAsync({
        id,
        status: nextStatus.key,
      });
    } catch (error) {
      // Status progression failed - silently handle
    } finally {
      setIsMovingToStep(false);
    }
  }, [
    id,
    nextStatus,
    invoice?.assigned_to_user,
    user,
    hasRole,
    buildInvoicePayload,
    updateInvoiceMutation,
    updateStatusMutation,
    unassignMutation,
  ]);

  // Assignment handlers
  const handleAssignToMe = useCallback(async () => {
    if (!id) return;
    await assignToMeMutation.mutateAsync({ id });
  }, [id, assignToMeMutation]);

  const handleUnassign = useCallback(async () => {
    if (!id) return;
    await unassignMutation.mutateAsync({ id });
  }, [id, unassignMutation]);

  // Delete handlers
  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!id) return;

    try {
      await deleteInvoiceMutation.mutateAsync({ id });
    } catch (error) {
      // Error is handled by the mutation's onError callback
    } finally {
      setShowDeleteConfirm(false);
    }
  }, [id, deleteInvoiceMutation]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  // Form data change handler
  const handleFormDataChange = useCallback(
    (updater: (prev: Partial<any>) => Partial<any>) => {
      setFormData(updater);
    },
    [setFormData]
  );

  // Early returns for loading and error states
  if (isDataLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice || !isDataReady) {
    return (
      <div className="flex justify-center items-center min-h-screen text-secondary-500">
        Invoice not found
      </div>
    );
  }

  return (
    <>
      <SplitPanelTemplate
        leftPanel={
          <InvoiceFormTemplate
            invoice={invoice}
            formData={formData}
            currentStatus={currentStatus}
            userRole={userWorkflowRole}
            currentUserId={user?.id}
            assignedToUserId={invoice.assigned_to}
            selectedStore={selectedStore}
            selectedSupplier={selectedSupplier}
            setSelectedSupplier={setSelectedSupplier}
            storesData={storesData}
            suppliersData={suppliersData}
            isLoadingStores={isLoadingStores}
            isLoadingSuppliers={isLoadingSuppliers}
            onInputChange={handleInputChange}
            onStoreChange={setSelectedStore}
            onFormDataChange={handleFormDataChange}
            onAssignToMe={handleAssignToMe}
            onUnassign={handleUnassign}
            canEditAddingStockFields={canEditAddingStockFields}
            canEditStockFields={() => canEditStockFields}
            canEditWorkflowFields={() => canEditWorkflowFields}
            hasRole={hasRole}
            isBasicFieldDisabled={() => isBasicFieldDisabled}
            isWorkflowFieldDisabled={isWorkflowFieldDisabled}
            isCreditNoteFieldDisabled={() => isCreditNoteFieldDisabled}
            isNotesFieldDisabled={() => isNotesFieldDisabled}
            wasUserPreviouslyAssigned={wasUserPreviouslyAssigned}
            isAssigning={assignToMeMutation.isPending}
            isUnassigning={unassignMutation.isPending}
            useWorkflowSystem={true} // Enable new workflow permission system
          />
        }
        rightPanel={
          pdfUrl ? (
            <PdfViewer invoice={invoice} pdfUrl={pdfUrl} isLoadingPdf={isLoadingPdf} />
          ) : (
            <NoPdfAvailable
              invoice={invoice}
              canUpload={hasRole('admin') || hasRole('finance')}
              onUploadClick={() => {
                // TODO: Implement PDF upload functionality
              }}
            />
          )
        }
        header={
          <InvoiceHeader
            invoice={invoice}
            currentStatus={currentStatus}
            statusDisplayName={statusDisplayName}
            nextStatus={nextStatus}
            nextStepButtonText={nextStepButtonText}
            hasOverrideRole={hasRole('admin') || hasRole('finance')}
            canDeleteInvoice={canDeleteInvoice}
            currentUserId={user?.id}
            assignedToUserId={invoice.assigned_to}
            onSave={handleSave}
            onCancel={handleCancel}
            onStatusUpdate={handleNextStatus}
            onStatusOverride={handleStatusOverride}
            onDelete={handleDeleteClick}
            updateStatusMutationPending={isMovingToStep}
            unassignMutationPending={unassignMutation.isPending}
            deleteInvoiceMutationPending={deleteInvoiceMutation.isPending}
            formData={formData}
            isLoading={isLoading}
          />
        }
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Invoice</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete invoice <strong>{invoice.invoice_number}</strong>{' '}
                  from <strong>{invoice.supplier}</strong>?
                </p>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This will permanently delete the invoice and all
                    associated data, including the PDF file and any workflow history.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleteInvoiceMutation.isPending}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteInvoiceMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleteInvoiceMutation.isPending ? 'Deleting...' : 'Delete Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
